import React, { useEffect, useState } from "react";
import AdminLayout from "./Common/AdminLayout";
import LoadingSkeleton from "./Common/LoadingSkeleton";
import { FaUnlock, FaLock, FaSearch, FaFileExport } from "react-icons/fa";
import { BsFillEyeFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import {
  ExportUser,
  FetcJobSeekerhUsers,
  UserStatus,
  UserCount,
} from "../../Redux/Slices/User";
import { BarLoader } from "react-spinners";
import toast from "react-hot-toast";
import "../../asset/new/css/admin-modern.css";

const JobSeekerList = () => {
  const { usersData, status } = useSelector((state) => state?.User);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Select
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(usersData.docs[0].data.map((user) => user._id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelect = (user) => {
    if (selectedUsers.includes(user._id)) {
      setSelectedUsers(selectedUsers.filter((id) => id !== user._id));
    } else {
      setSelectedUsers([...selectedUsers, user._id]);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setDataLoading(true);
    let data = {
      search: e.target.value,
      role: "Jobseeker",
      page: 1,
    };
    dispatch(FetcJobSeekerhUsers(data));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const exportUser = async () => {
    try {
      setLoading(true);
      if (selectedUsers.length > 0) {
        await ExportUser("Jobseeker", selectedUsers);
        setLoading(false);
        toast.success("User exported successfully");
      } else {
        await ExportUser("Jobseeker");
        setLoading(false);
        toast.success("User exported successfully");
      }
    } catch (error) {
      setLoading(false);
      toast.error("Failed to export user");
    }
  };

  // Block and Unblock
  const userStatushandler = async (id, value) => {
    const userClickedOK = await window.confirm(
      "Are you sure you want to proceed?"
    );

    if (userClickedOK) {
      let action = { action: value };
      await UserStatus(id, action);
      let data = {
        role: "Jobseeker",
      };
      dispatch(FetcJobSeekerhUsers(data));
    }
  };

  useEffect(() => {
    let data;
    let prevPage = localStorage.getItem("prevPage");
    if (prevPage) {
      data = JSON.parse(prevPage);
    } else {
      data = {
        role: "Jobseeker",
        page: 1,
      };
    }
    dispatch(FetcJobSeekerhUsers(data));
    document.title = "Jobseekers | Admin";
    
    // Fetch total count
    const fetchCount = async () => {
      try {
        const countData = await UserCount();
        setTotalCount(countData?.jobseekerCount || 0);
      } catch (error) {
        console.error("Error fetching count:", error);
      }
    };
    fetchCount();
  }, [dispatch]);

  useEffect(() => {
    // Only set dataLoading to false when we have actual data or when status is not loading
    if (usersData && usersData.docs && isInitialLoad) {
      setTimeout(() => {
        setDataLoading(false);
        setIsInitialLoad(false);
      }, 500);
    } else if (usersData && usersData.docs && !isInitialLoad) {
      setDataLoading(false);
    }
    
    // If API call failed or completed but returned error
    if (status === "idel" || status === "fullfilled") {
      setDataLoading(false);
      setIsInitialLoad(false);
    }
  }, [usersData, isInitialLoad, status]);

  const handlePageChange = async (page) => {
    setDataLoading(true);
    let data = {
      role: "Jobseeker",
      page: page,
      search: searchQuery,
    };
    dispatch(FetcJobSeekerhUsers(data));
    localStorage.setItem("prevPage", JSON.stringify(data));
  };

  // Calculate visible page range
  let startPage = 1;
  let endPage = 1;

  if (usersData?.pagination) {
    startPage = Math.max(1, usersData.pagination.page - 2);
    endPage = Math.min(
      usersData.pagination.totalPages,
      usersData.pagination.page + 2
    );

    if (usersData.pagination.page <= 3) {
      endPage = Math.min(5, usersData.pagination.totalPages);
    } else if (
      usersData.pagination.page >=
      usersData.pagination.totalPages - 2
    ) {
      startPage = Math.max(1, usersData.pagination.totalPages - 4);
    }
  }

  return (
    <AdminLayout
      title="Job Seekers List"
      subtitle="Manage all registered job seekers"
      breadcrumbs={[
        { label: "Admin", active: false },
        { label: "Job Seekers", active: true }
      ]}
    >
      {/* Table Card */}
      <div className="admin-table-wrapper">
        <div className="admin-table-header">
          <h3 className="admin-table-title">
            All Job Seekers ({totalCount || 0})
          </h3>
          <div className="admin-table-actions">
            <div className="admin-table-search">
              <FaSearch className="admin-table-search-icon" size={14} />
                    <input
                      type="search"
                      placeholder="Search by name, email..."
                      value={searchQuery}
                      onChange={handleSearch}
                    />
                  </div>
                  <button 
                    onClick={exportUser} 
                    className="admin-btn admin-btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <BarLoader color="white" width={50} height={2} />
                    ) : (
                      <>
                        <FaFileExport /> Export
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="admin-table-container">
                {(dataLoading || status === "Loading......" || !usersData?.docs) ? (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>Select</th>
                        <th style={{ width: '80px' }}>S.No</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th style={{ width: '200px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <LoadingSkeleton type="table-row" count={5} columns={6} />
                    </tbody>
                  </table>
                ) : usersData?.docs && usersData?.docs[0]?.data?.length > 0 ? (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th style={{ width: '80px' }}>S.No</th>
                        <th onClick={() => handleSort("name")} style={{ cursor: 'pointer' }}>
                          Name
                        </th>
                        <th onClick={() => handleSort("email")} style={{ cursor: 'pointer' }}>
                          Email
                        </th>
                        <th>Phone</th>
                        <th style={{ width: '200px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersData.docs[0].data.map((user, index) => (
                        <tr key={user._id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectAll || selectedUsers.includes(user._id)}
                              onChange={() => handleSelect(user)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td>{index + 1}</td>
                          <td style={{ fontWeight: 500 }}>{user?.name}</td>
                          <td>{user?.email}</td>
                          <td>{user?.mobile}</td>
                          <td>
                            <div className="admin-flex admin-gap-sm">
                              {user?.status === "Active" ? (
                                <button
                                  onClick={() => userStatushandler(user?._id, "Inactive")}
                                  className="admin-btn admin-btn-sm admin-btn-danger"
                                  title="Block User"
                                >
                                  <FaLock /> Block
                                </button>
                              ) : (
                                <button
                                  onClick={() => userStatushandler(user?._id, "Active")}
                                  className="admin-btn admin-btn-sm admin-btn-success"
                                  title="Unblock User"
                                >
                                  <FaUnlock /> Unblock
                                </button>
                              )}
                              <a
                                href={`/Jobseeker/${user?._id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admin-btn admin-btn-sm admin-btn-primary"
                              >
                                <BsFillEyeFill /> View
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="admin-empty-state">
                    <div className="admin-empty-state-icon">
                      <FaSearch />
                    </div>
                    <h3 className="admin-empty-state-title">No Job Seekers Found</h3>
                    <p className="admin-empty-state-description">
                      {searchQuery 
                        ? "Try adjusting your search criteria" 
                        : "No job seekers have registered yet"}
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {usersData?.pagination && usersData?.docs[0]?.data?.length > 0 && (
                <div className="admin-table-footer">
                  <div className="admin-table-info">
                    {(() => {
                      const currentPage = usersData.pagination.page || 1;
                      const pageLimit = usersData.pagination.limit || usersData.docs[0].data.length || 10;
                      const total = totalCount || 0;
                      const startItem = ((currentPage - 1) * pageLimit) + 1;
                      const endItem = Math.min(currentPage * pageLimit, total);
                      
                      return `Showing ${startItem} to ${endItem} of ${total} results`;
                    })()}
                  </div>
                  <div className="admin-pagination">
                    <button
                      className="admin-pagination-item"
                      onClick={() => handlePageChange(usersData.pagination.prevPage)}
                      disabled={!usersData.pagination.prevPage}
                    >
                      Previous
                    </button>
                    {Array.from(
                      { length: endPage - startPage + 1 },
                      (_, i) => startPage + i
                    ).map((page) => (
                      <button
                        key={page}
                        className={`admin-pagination-item ${
                          usersData.pagination.page === page ? "active" : ""
                        }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="admin-pagination-item"
                      onClick={() => handlePageChange(usersData.pagination.nextPage)}
                      disabled={!usersData.pagination.nextPage}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
    </AdminLayout>
  );
};

export default JobSeekerList;
